import { useState, type ReactNode } from 'react';
import type { MediaFilter } from '@/utils/media';
import { regions, years } from '@/utils/media';

interface FilterBarProps {
  genreOptions: string[];
  showStatus: boolean;
  filter: MediaFilter;
  onChange: (next: MediaFilter) => void;
  onReset: () => void;
}

function FilterRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 py-3 md:flex-row md:items-start md:gap-4">
      <span className="w-14 shrink-0 pt-1 text-sm font-medium text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors ${
        active
          ? 'bg-primary-500 text-background-50'
          : 'bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-700'
      }`}
    >
      {children}
    </button>
  );
}

export default function FilterBar({ genreOptions, showStatus, filter, onChange, onReset }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const toggleGenre = (g: string) => {
    const genres = filter.genres.includes(g)
      ? filter.genres.filter((item) => item !== g)
      : [...filter.genres, g];
    onChange({ ...filter, genres });
  };

  const toggleRegion = (r: string) => {
    const next = filter.regions.includes(r)
      ? filter.regions.filter((item) => item !== r)
      : [...filter.regions, r];
    onChange({ ...filter, regions: next });
  };

  const toggleYear = (y: number) => {
    const next = filter.years.includes(y)
      ? filter.years.filter((item) => item !== y)
      : [...filter.years, y];
    onChange({ ...filter, years: next });
  };

  const activeCount = filter.genres.length + filter.regions.length + filter.years.length;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4">
      <div className="flex flex-col gap-3 border-b border-background-200 pb-3 md:flex-row md:items-center md:justify-between">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 whitespace-nowrap text-sm font-medium text-foreground-800 md:hidden"
        >
          <i className="ri-filter-3-line text-lg text-primary-600"></i>
          筛选条件
          {activeCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1 text-[11px] text-background-50">
              {activeCount}
            </span>
          )}
          <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} ml-auto text-lg`}></i>
        </button>

        <div className="hidden items-center gap-2 text-sm text-foreground-500 md:flex">
          <i className="ri-equalizer-line text-primary-600"></i>
          共筛选 {activeCount} 个条件
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 rounded-md bg-background-100 p-0.5">
            {(['updated', 'hits', 'rating'] as const).map((s) => (
              <button
                key={s}
                onClick={() => onChange({ ...filter, sort: s })}
                className={`whitespace-nowrap rounded px-3 py-1.5 text-sm transition-colors ${
                  filter.sort === s ? 'bg-background-50 font-medium text-primary-600' : 'text-foreground-600'
                }`}
              >
                {s === 'updated' ? '更新时间' : s === 'hits' ? '热度' : '评分'}
              </button>
            ))}
          </div>
          <button
            onClick={onReset}
            className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-600 hover:border-accent-300 hover:text-accent-600"
          >
            <i className="ri-refresh-line"></i> 重置
          </button>
        </div>
      </div>

      <div className={`${open ? 'block' : 'hidden'} divide-y divide-background-200 md:block md:divide-y-0`}>
        <FilterRow label="类型">
          <Chip active={filter.genres.length === 0} onClick={() => onChange({ ...filter, genres: [] })}>
            全部
          </Chip>
          {genreOptions.map((g) => (
            <Chip key={g} active={filter.genres.includes(g)} onClick={() => toggleGenre(g)}>
              {g}
            </Chip>
          ))}
        </FilterRow>
        <FilterRow label="地区">
          <Chip active={filter.regions.length === 0} onClick={() => onChange({ ...filter, regions: [] })}>
            全部
          </Chip>
          {regions.map((r) => (
            <Chip key={r} active={filter.regions.includes(r)} onClick={() => toggleRegion(r)}>
              {r}
            </Chip>
          ))}
        </FilterRow>
        <FilterRow label="年份">
          <Chip active={filter.years.length === 0} onClick={() => onChange({ ...filter, years: [] })}>
            全部
          </Chip>
          {years.map((y) => (
            <Chip key={y} active={filter.years.includes(y)} onClick={() => toggleYear(y)}>
              {y}
            </Chip>
          ))}
        </FilterRow>
        {showStatus && (
          <FilterRow label="状态">
            <Chip active={filter.status === 'all'} onClick={() => onChange({ ...filter, status: 'all' })}>
              全部
            </Chip>
            <Chip active={filter.status === '连载中'} onClick={() => onChange({ ...filter, status: '连载中' })}>
              连载中
            </Chip>
            <Chip active={filter.status === '已完结'} onClick={() => onChange({ ...filter, status: '已完结' })}>
              已完结
            </Chip>
          </FilterRow>
        )}
      </div>
    </div>
  );
}